import React, { useState } from 'react';
import { Shield, Lightbulb, BookOpen, Calendar, Search, Share2, ArrowLeft, LucideIcon } from 'lucide-react';
import Navbar from '../components/Navbar';
import ReactMarkdown from 'react-markdown';

interface Category {
  id: string;
  name: string;
  icon: LucideIcon | null;
}

interface Article {
  id: number;
  title: string;
  category: string;
  date: string;
  excerpt: string;
  content: string;
  image?: string;
}

const categories: Category[] = [
  { id: 'all', name: 'Tous', icon: null },
  { id: 'security', name: 'Conseils cybersécurité', icon: Shield },
  { id: 'tech', name: 'Nouveautés technologiques', icon: Lightbulb },
  { id: 'tutorial', name: 'Articles pédagogiques', icon: BookOpen },
  { id: 'events', name: 'Événements / Formations', icon: Calendar }
];

const articles: Article[] = [
  {
    id: 1,
    title: 'Les bonnes pratiques pour sécuriser vos mots de passe',
    category: 'security',
    date: '',
    excerpt: 'Découvrez les meilleures pratiques pour créer et gérer des mots de passe sécurisés dans votre entreprise...',
    content: `
La sécurité de vos comptes commence par des mots de passe solides. Voici les règles essentielles à suivre :

### 1. Longs et complexes
- Utilisez au moins 12 caractères  
- Combinez lettres majuscules et minuscules  
- Incluez des chiffres et des symboles  
- Évitez les suites logiques ou les mots du dictionnaire  

### 2. Pas de mots évidents
- Évitez les mots du dictionnaire  
- Ne pas utiliser les dates de naissance  
- Évitez les informations personnelles facilement devinables  
- Ne pas utiliser des mots basés sur des noms ou des lieux  

### 3. Un mot de passe unique par compte
- Créez un mot de passe différent pour chaque service  
- Limitez les risques en cas de fuite  
- Évitez l'effet domino si un compte est compromis  

### 4. Gestionnaire de mots de passe
- Pour générer des mots de passe forts  
- Pour stocker vos accès de façon sécurisée  
- Pour gérer facilement vos différents comptes  
- Pour synchroniser vos mots de passe entre vos appareils  

### 5. Activez la double authentification (2FA)
- Ajoutez une protection supplémentaire  
- Utilisez une application d'authentification ou une clé physique  
- Évitez l'authentification par SMS quand c'est possible  

### 6. Changez-les si nécessaire
- En cas de doute sur la sécurité  
- Après une fuite de données connue  
- Si vous détectez une activité suspecte  
- Selon la politique de sécurité de votre entreprise  

### 7. Ne partagez jamais vos mots de passe
- Ne les transmettez pas par email ou SMS  
- Ne les stockez pas dans des documents non sécurisés  
- Utilisez des méthodes sécurisées pour le partage  
- Éduquez vos collaborateurs sur ces bonnes pratiques  

En suivant ces règles, vous renforcez significativement la sécurité de vos comptes et protégez vos données sensibles.
    `,
  },
  {
    id: 2,
    title: 'L\'IA dans la cybersécurité',
    category: 'tech',
    date: '',
    excerpt: 'Comment l\'intelligence artificielle transforme la sécurité informatique et protège votre infrastructure...',
    content: `
L'IA dans la cybersécurité : alliée de la défense numérique

### 1. Détection en temps réel
• Analyse les comportements anormaux  
• Identifie les menaces dès leur apparition  
• Réagit plus vite que les méthodes classiques  

### 2. Automatisation des réponses
• Blocage automatique d'activités suspectes  
• Isolation rapide des postes infectés  
• Réduction du temps de réaction aux incidents  

### 3. Analyse prédictive
• Anticipe les attaques grâce au traitement massif de données  
• Détecte des tendances malveillantes avant qu'elles ne causent des dommages  
• Lutte contre les menaces avancées  

### 4. Aide à contrer le phishing, les malwares évolutifs, et les attaques ciblées
• S'adapte aux techniques des cybercriminels  
• Limites et vigilance  
• Risques de faux positifs ou d'erreurs d'interprétation  

### 5. Les attaquants utilisent aussi l'IA
• Deepfakes  
• IA malveillantes  
• L'humain reste essentiel pour superviser et corriger
    `,
  },
  {
    id: 3,
    title: 'Comprendre les protocoles de sécurité réseau',
    category: 'tutorial',
    date: '',
    excerpt: 'Guide complet sur les protocoles de sécurité essentiels pour protéger votre réseau d\'entreprise...',
    content: `
Les protocoles de sécurité protègent les échanges de données sur les réseaux. Ils assurent confidentialité, intégrité et authenticité des communications.

### 1. SSL/TLS – Sécurisation des connexions web
• Chiffre les échanges entre navigateur et serveur  
• Utilisé par HTTPS  
• Protège contre les interceptions (ex : attaques "man-in-the-middle")  

### 2. IPSec – Sécurité au niveau IP
• Assure le chiffrement et l'authentification des paquets IP  
• Utilisé dans les VPNs pour sécuriser les connexions distantes  

### 3. HTTPS – HTTP sécurisé
• Version sécurisée de HTTP via TLS  
• Protège les données échangées sur les sites web  
• Gage de confiance pour les utilisateurs  

### 4. SSH – Connexion sécurisée à distance
• Permet d'administrer un serveur ou un système à distance de manière chiffrée  
• Alternative sécurisée à Telnet  

### 5. WPA2 / WPA3 – Sécurité des réseaux Wi-Fi
• Chiffre les communications sans fil  
• Protège les réseaux domestiques et professionnels  
• WPA3 offre une meilleure résistance aux attaques par force brute  

### 6. RADIUS & TACACS+ – Contrôle d'accès aux équipements réseau
• Authentifient les utilisateurs qui se connectent aux équipements  
• Utilisés dans les grandes infrastructures pour centraliser la gestion des accès
    `,
  },
  {
    id: 4,
    title: 'Formation CCNA Security',
    category: 'events',
    date: '',
    excerpt: 'Inscrivez-vous à notre prochaine session de formation CCNA Security et devenez expert en sécurité réseau...',
    content: `
La certification CCNA Security (Cisco Certified Network Associate Security) est une formation reconnue qui prépare à protéger les réseaux informatiques contre les menaces et à maîtriser les solutions de sécurité Cisco.

### 1. Objectifs de la formation
• Comprendre les fondamentaux de la sécurité réseau  
• Apprendre à sécuriser les routeurs, switches et accès réseau  
• Maîtriser les protocoles et outils de sécurité Cisco  
• Préparer les administrateurs à réagir face aux cybermenaces  

### 2. Thèmes abordés
• Contrôle d'accès : ACL, AAA (Authentication, Authorization, Accounting)  
• Pare-feux Cisco (CBAC, ZPF)  
• VPN : principes et configuration IPSec  
• Sécurisation des équipements et du plan de contrôle  
• Détection et prévention des intrusions (IDS/IPS)  
• Bonnes pratiques de gestion et de réponse aux incidents  

### 3. Compétences développées
• Configurer des équipements Cisco avec des règles de sécurité  
• Appliquer des stratégies de sécurité réseau  
• Utiliser des outils de surveillance et d'analyse  
• Diagnostiquer et résoudre des problèmes de sécurité  

### 4. Public ciblé
• Techniciens réseau  
• Administrateurs système  
• Étudiants en cybersécurité  
• Toute personne souhaitant une base solide en sécurité Cisco  

### 5. Pré-requis
• Connaissances en réseau de niveau CCNA Routing & Switching  
• Compréhension des modèles OSI et TCP/IP
    `,
  }
];

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  const filteredArticles = articles.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const renderArticleDetail = (article: Article) => (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white shadow-xl rounded-2xl p-6">
        <button
          onClick={() => setSelectedArticle(null)}
          className="flex items-center text-emerald-500 hover:text-rose-600 mb-6 transition-colors duration-300"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Retour au blog
        </button>

        <div className="flex items-center gap-4 mb-4">
          <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-sm">
            {categories.find(cat => cat.id === article.category)?.name}
          </span>
          <span className="text-gray-500">{article.date}</span>
        </div>

        <h1 className="text-3xl font-bold mb-6 text-gray-800">{article.title}</h1>

        <div className="prose prose-lg max-w-none text-gray-700">
          <ReactMarkdown>{article.content}</ReactMarkdown>
        </div>
      </div>
    </div>
  );

  if (selectedArticle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navbar />
        {renderArticleDetail(selectedArticle)}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navbar />

      <section className="bg-gradient-to-br from-emerald-400 via-rose-400 to-red-400 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Nos actualités & conseils</h1>
            <p className="text-xl text-white/90">
              Restez informé sur la cybersécurité, les nouvelles technologies et les événements GoSecuNet
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
              />
              <Search className="w-5 h-5 text-white/60 absolute right-4 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-white text-emerald-600'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {category.icon && <category.icon className="w-4 h-4 inline-block mr-2" />}
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto gap-6 pb-4 scrollbar-hide">
            {filteredArticles.map(article => (
              <div
                key={article.id}
                className="flex-none w-[350px] bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-emerald-200 group"
                onClick={() => setSelectedArticle(article)}
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-sm font-medium">
                      {categories.find(cat => cat.id === article.category)?.name}
                    </span>
                    <span className="text-gray-500 text-sm">{article.date}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-800 group-hover:text-rose-600 transition-colors duration-300">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{article.excerpt}</p>
                  <button className="text-emerald-600 hover:text-rose-600 font-medium flex items-center transition-colors duration-300">
                    Lire plus
                    <ArrowLeft className="w-4 h-4 ml-2 transform rotate-180 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
